import Header from "@components/Header/Header"
import { useNavigate, useLocation } from "react-router"
import { useEffect, useState } from "react"
import { RedeemTab } from "@components/TabSelector/TabSelector"
import { E_WALLET_REDEMPTION_LIMIT } from "@constants/transaction"
import RedeemBank from "./Bank"
import RedeemEWallet from "./EWallet"
import { Container, TabContainer } from "./Redeem.styles"
import { useTranslation } from "react-i18next"
import useBankManagement from "@hooks/useManageBank"
import useEWalletManagement from "@hooks/useManageEWallet"
import LoadingModal from "@components/LoadingModal/LoadingModal"
import useRedemptionLimit from "@hooks/useRedemptionLimit"
import useBalance from "@hooks/useBalance"
import { RedemptionType } from "../../types/redemption"
import { setHeaderColor } from "@lib/telegram"
import { WHITE } from "@styles/colors"
import { useUserRestrictions } from "@hooks/useUserRestrictions"

const Redeem = () => {
  const { supportedBanks, bankAsRedemptionOption, isLoading: isLoadingBanks } = useBankManagement()
  const { supportedEWallets, ewalletAsRedemptionOption, isLoading: isLoadingEWallets } = useEWalletManagement()
  const { data: redemptionLimit, isLoading: isLoadingRedemptionLimit } = useRedemptionLimit()
  const { data: balance, isLoading: isLoadingBalance } = useBalance()
  const { canRedeemToEwallet } = useUserRestrictions()

  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const queryParams = new URLSearchParams(location.search)
  const tab = queryParams.get('tab')
  const [activeTab, setActiveTab] = useState<RedemptionType>(tab?.toLocaleUpperCase() as RedemptionType || 'BANK')

  // E-Wallet Redemption Limit
  const ewalletRemainingLimit = redemptionLimit && Number(redemptionLimit.redeem_request_remaining)
  const preselectedEWallet = ewalletAsRedemptionOption?.length > 0 ? ewalletAsRedemptionOption.find(ewallet => ewallet.id === location.state?.ewallet_uuid) : undefined
  let ewalletTotalDailyLimit = E_WALLET_REDEMPTION_LIMIT
  if (preselectedEWallet) {
    if (supportedEWallets) {
      ewalletTotalDailyLimit = supportedEWallets.find(ewallet => ewallet.provider === preselectedEWallet.provider)?.max_amount || E_WALLET_REDEMPTION_LIMIT
    }
  } else {
    if (supportedEWallets) {
      ewalletTotalDailyLimit = Math.min(...supportedEWallets.map(wallet => wallet.max_amount))
    }
  }

  // Bank Redemption Limit
  const bankRemainingLimit = redemptionLimit && Number(redemptionLimit.redeem_request_remaining)
  const bankTotalDailyLimit = redemptionLimit && Number(redemptionLimit.total_redeem_request_limit)
  const preselectedBank = bankAsRedemptionOption.find(bank => bank.id === location.state?.bank_uuid)
  const prefilledAmount = location.state?.amount as number | undefined
  const splitDisbursementCountLimit = redemptionLimit && redemptionLimit.maximum_split_disbursements

  useEffect(() => {
    setHeaderColor(WHITE)
  }, [])

  return (
    <Container>
      <Header title={t('redeem.header')} showBackButton onBackClick={() => navigate('/home')}>
        {canRedeemToEwallet() && <TabContainer>
          <RedeemTab activeTab={activeTab} setActiveTab={setActiveTab} />
        </TabContainer>}
      </Header>

      <LoadingModal
        visible={
          isLoadingBanks ||
          isLoadingEWallets ||
          isLoadingRedemptionLimit ||
          isLoadingBalance
        }
      />

      {
        !isLoadingBanks &&
        activeTab === 'BANK' &&
        <RedeemBank
          currentBalance={balance!.confirmed_balance}
          remainingLimit={bankRemainingLimit}
          totalDailyLimit={bankTotalDailyLimit}
          preselectedBank={preselectedBank}
          prefilledAmount={prefilledAmount}
          redemptionOptions={bankAsRedemptionOption || []}
          supportedBanks={supportedBanks}
          splitDisbursementCountLimit={splitDisbursementCountLimit}
        />
      }

      {
        !isLoadingEWallets &&
        activeTab === 'E-WALLET' &&
        <RedeemEWallet
          currentBalance={balance!.confirmed_balance}
          remainingLimit={ewalletRemainingLimit}
          totalDailyLimit={ewalletTotalDailyLimit}
          preselectedEWallet={preselectedEWallet}
          prefilledAmount={prefilledAmount}
          redemptionOptions={ewalletAsRedemptionOption || []}
          supportedEWallets={supportedEWallets}
        />
      }

    </Container>
  )
}

export default Redeem